/**
 * The remaining CRM domains — accounts, contacts, opportunities, quotations,
 * activities, reports, master data and administration.
 *
 * `docs/API.md` §5–§17. **None are implemented server-side yet.** Each function
 * calls the real endpoint and falls back to a fixture only on
 * `501 not_implemented`, so the screens work today and switch to live data the
 * moment a module lands — with no edit here or in the components.
 *
 * These live in one file rather than nine because each is currently a thin
 * call-and-fallback with nothing domain-specific to say. Split them out as each
 * gains real query building, cache keys and mutations; the import sites are
 * already per-domain hooks, so splitting will not reach the components.
 */
import apiClient from './axiosClient.js';
import { withMockFallback, paginate, applyFilters } from './mockFallback.js';
import { contacts } from './mock/contacts.fixture.js';
import {
  dashboards, reports, auditTrail, workflows, scoringRules,
  securityPolicy,
} from './mock/crm.fixture.js';

/* §5 Accounts and Contacts — split out into api/accounts.api.js and
   api/contacts.api.js respectively. Both are real, fully-implemented modules
   now (src/modules/accounts, src/modules/contacts on the API side), not a
   thin call-and-fallback, so neither belongs in this catch-all any more.
   `listOccasions` below stays here — the dashboard's AnniversariesCard is a
   separate, broader piece of work from the contacts screen. */

/**
 * Contacts with a birthday or work anniversary in the window, so a rep can send
 * a greeting on the day rather than a week late.
 *
 * Dates are compared as plain ISO day strings, never as timestamps: a contact in
 * Auckland must not get a birthday email a day early because the server runs in
 * UTC.
 *
 * @param {{within_days?:number}} params
 */
export function listOccasions({ within_days: withinDays = 30 } = {}) {
  return withMockFallback(
    /* §5 names this `anniversaries`. It was `occasions` here, which no route
       serves — and because `/contacts/:id` matched it first, the answer was a
       422 about a malformed uuid rather than the 501 that triggers this
       fallback, so the card rendered "nothing in the next 30 days" for ever. */
    'GET /contacts/anniversaries',
    async () => (await apiClient.get('/contacts/anniversaries', { params: { within_days: withinDays } })).data,
    () => ({ data: computeOccasions(contacts, withinDays) }),
  );
}

/**
 * Birthdays and anniversaries falling inside the next `withinDays`.
 *
 * Works on month/day only, so it wraps the year end correctly — a birthday on
 * 2 January is "in 12 days" when today is 21 December, not "355 days ago".
 *
 * @param {object[]} rows contacts carrying `birthday` / `joinedCompany` / `customerSince`
 * @param {number} withinDays
 */
function computeOccasions(rows, withinDays) {
  const today = new Date();
  const startOfToday = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const out = [];

  const kinds = [
    { field: 'birthday', label: 'Birthday', tone: 'rose' },
    { field: 'joinedCompany', label: 'Work anniversary', tone: 'violet' },
    { field: 'customerSince', label: 'Customer anniversary', tone: 'teal' },
  ];

  for (const contact of rows) {
    for (const kind of kinds) {
      const iso = contact[kind.field];
      if (!iso) continue;
      const [year, month, day] = iso.split('-').map(Number);

      // Try this year, then next — that is what makes the year boundary work.
      let next = Date.UTC(today.getUTCFullYear(), month - 1, day);
      if (next < startOfToday) next = Date.UTC(today.getUTCFullYear() + 1, month - 1, day);

      const inDays = Math.round((next - startOfToday) / 86_400_000);
      if (inDays > withinDays) continue;

      out.push({
        contactId: contact.id,
        name: contact.name,
        company: contact.company,
        kind: kind.label,
        tone: kind.tone,
        date: iso,
        inDays,
        years: new Date(next).getUTCFullYear() - year,
      });
    }
  }

  return out.sort((a, b) => a.inDays - b.inDays);
}

/* §6 Opportunities & pipeline — split out into api/opportunities.api.js and
   api/pipeline.api.js respectively. Both are real, fully-implemented modules
   now (src/modules/opportunities on the API side), not a thin
   call-and-fallback, so neither belongs in this catch-all any more. */

/* §7 Quotations & approvals — split out into api/quotations.api.js and
   api/approvals.api.js respectively. Both are real, fully-implemented modules
   now (src/modules/quotations, src/modules/approvals on the API side), not a
   thin call-and-fallback, so neither belongs in this catch-all any more. */

/* §8 Activities — split out into api/activities.api.js. It is a real,
   fully-implemented module now (src/modules/activities on the API side), not
   a thin call-and-fallback, so it no longer belongs in this catch-all.
   Follow-ups are api/followups.api.js — a real module too
   (src/modules/followups). There is no separate feed endpoint: the frontend's
   feed panels call activities.api.js's listActivities directly, recent-first
   — see ActivitiesPage.jsx and ActivitiesPages.jsx's FeedPage. */

/* -------------------------------------------------------------------------- */
/* §13 Dashboards & reports                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The KPI tiles and charts for one dashboard.
 *
 * Which dashboard a user lands on comes from their highest-priority role's
 * `landing_screen`, not from a preference — a rep covering as a manager should
 * see the manager view for the duration.
 *
 * @param {'rep'|'manager'|'exec'|'admin'} variant
 */
export function getDashboard(variant) {
  return withMockFallback(
    `GET /dashboards/${variant}`,
    async () => (await apiClient.get(`/dashboards/${variant}`)).data,
    () => dashboards[variant] ?? dashboards.rep,
  );
}

/**
 * Quota attainment per owner for the period.
 *
 * `forecast`/`lead-funnel`/`territories` used to live here too — all three
 * are now real reports at `/analytics/*` (`api/reports.api.js`,
 * `features/reports/useReports.js`), not this fixture-backed shape. Targets
 * stays: it's a CRUD screen, not a report — see `reports.model.md` §6 — and
 * `/v1/targets` is still unbuilt, so this is still mock-only.
 */
export function getTargets(params = {}) {
  return withMockFallback(
    'GET /targets',
    async () => (await apiClient.get('/targets', { params })).data,
    () => ({ data: reports.targets }),
  );
}

/* -------------------------------------------------------------------------- */
/* §10 Master data                                                             */
/* -------------------------------------------------------------------------- */

/**
 * One master-data list — sources, statuses, industries, currencies, products.
 *
 * `usage` is the count of records referencing each entry. It is what lets the UI
 * offer "deactivate" instead of "delete" when a value is in use: deleting would
 * answer `409 master_data_in_use`, and an admin needs to know that before they
 * click, not after.
 *
 * @param {string} entity e.g. 'lead_source', 'industry', 'currency', 'product'
 */
/* §10 master data lives in `masterData.api.js` — it is a built endpoint with a
   field catalogue the admin screen generates its forms from, so it needs a real
   module rather than a fixture-backed pair of helpers. */

/* -------------------------------------------------------------------------- */
/* §12 Audit · §14 Automation · §2 Security                                    */
/* -------------------------------------------------------------------------- */

/**
 * The audit trail.
 *
 * `audit.*` is append-only and hash-chained in the database, so these rows can
 * be read but never edited or deleted from the application — the UI must offer
 * no such control.
 *
 * @param {{limit?:number, cursor?:string, actor?:string, action?:string, q?:string}} params
 */
export function listAuditTrail(params = {}) {
  return withMockFallback(
    'GET /audit',
    async () => (await apiClient.get('/audit', { params })).data,
    () =>
      paginate(
        applyFilters(auditTrail, params, {
          search: ['actor', 'record', 'detail'],
          equals: ['action', 'result', 'entity'],
        }),
        params,
        (r) => String(r.id),
      ),
  );
}

/** Automation rules and how often each has fired. */
export function listWorkflows(params = {}) {
  return withMockFallback(
    'GET /workflows',
    async () => (await apiClient.get('/workflows', { params })).data,
    () => ({ data: workflows }),
  );
}

/** The configurable lead-scoring rules behind the score on every lead. */
export function listScoringRules() {
  return withMockFallback(
    'GET /scoring-rules',
    async () => (await apiClient.get('/scoring-rules')).data,
    () => ({ data: scoringRules }),
  );
}

/**
 * The organisation's security policy — password rules, lockout, session limits.
 * One row per organisation (`iam.security_policy`), so this is a fetch, not a list.
 */
export function getSecurityPolicy() {
  return withMockFallback(
    'GET /security-policy',
    async () => (await apiClient.get('/security-policy')).data,
    () => securityPolicy,
  );
}

/* §8 Email templates — split out into api/emailTemplates.api.js. It is a real,
   fully-implemented module now (src/modules/emailTemplates on the API side),
   not a thin call-and-fallback, so it no longer belongs in this catch-all. */
